import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ResCurrency } from '@app/reservation/models/res-owner.model';
import { CoreUtilityDialog, EidosUtilityService } from '@app/core/services/eidos-utility.service';
import { CoreModalService } from '@app/core/services/core-modal.service';
import { DateTime } from 'luxon';
import { ReservationPermission } from '../models/reservation-permission.model';
import { IEidosEnvironment } from '@app/core/config/environment.interface';
import { EidosConfigService } from '@app/core/config/eidos-config.service';
import { IReservationCompany } from '@app/reservation/models/res-cached-data.models';
import { ReservationProductCalendarVoyage } from '@app/reservation/models/res-product.models';
import {
  isAkCompanies,
  isAkTocCompanies,
  isAKAustraliaTocCompany,
  isCoxAndKingAustraliaCompany,
  isCoxAndKingCompanies,
  isCoxAndKingUkCompany,
  isCrystalCompany,
  isHeritageCompany,
  isAkSanctuaryPaCompany,
  isAKUkTocCompany,
  isAkUsaTocCompany,
  isAkTanzaniaDmcCompany,
  isAkSouthAfricaDmcCompany,
} from '../utils/rescom-company.utils';

@Injectable({
  providedIn: 'root'
})
export class RescomService {

  utilityDialogs: CoreUtilityDialog;

  public uniqueContainer = new BehaviorSubject<{ category: string, key: string }>({ category: '', key: '' })

  onSelectedCalendarVoyageChange = new BehaviorSubject<ReservationProductCalendarVoyage | null>(null);
  public allAvailableCompanies: BehaviorSubject<IReservationCompany[]> = new BehaviorSubject<IReservationCompany[]>([]);
  public allAvailableSortedCompanies: BehaviorSubject<Array<IReservationCompany>> = new BehaviorSubject<Array<IReservationCompany>>([]);

  constructor(
    private coreModalService: CoreModalService,
    private coreUtilityService: EidosUtilityService,
    private eidosConfigService: EidosConfigService,
  ) {
    this.utilityDialogs = new CoreUtilityDialog(this.coreModalService);
    this.config = this.eidosConfigService.currentConfig.getValue();
  }


  isCrystal(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isCrystalCompany(companyID);
  }
  isAkCompanies(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkCompanies(companyID);
  }
  isAkTocCompanies(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkTocCompanies(companyID);
  }
  isCoxAndKing(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isCoxAndKingCompanies(companyID);
  }
  isCoxAndKingUk(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isCoxAndKingUkCompany(companyID);
  }
  isCoxAndKingAustralia(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isCoxAndKingAustraliaCompany(companyID);
  }
  isAustralia(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAKAustraliaTocCompany(companyID);
  }
  isUk(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAKUkTocCompany(companyID);
  }
  isUsa(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkUsaTocCompany(companyID);
  }
  isHeritageClub(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isHeritageCompany(companyID);
  }
  isAkSanctuaryPa(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkSanctuaryPaCompany(companyID);
  }
  isAkSouthAfricaDmc(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkSouthAfricaDmcCompany(companyID);
  }
  isAkTanzaniaDmc(companyID = this.currentCompany.getValue()?.CompanyID) {
    return isAkTanzaniaDmcCompany(companyID);
  }

  public getAvailableCompanies() {
    return this.coreUtilityService.getAvailableCompanies();
  }

  allValidCurrencies: BehaviorSubject<Array<ResCurrency>> = new BehaviorSubject<Array<ResCurrency>>([]);

  currentReservationPermission: BehaviorSubject<ReservationPermission> = new BehaviorSubject<ReservationPermission>(ReservationPermission.emptyPermission());

  companyDataReload = new BehaviorSubject<boolean>(false);

  protected config: IEidosEnvironment | undefined;

  public static buildDateRange(d1: DateTime, d2?: DateTime) {
    if (!d1 && !d2) return [];
    if (!d2) return [d1];
    let days = d2.diff(d1, 'days').days;
    let dateRange = [];
    for (let i = 0; i <= days; i++) {
      dateRange.push(d1.plus({ days: i }));
    }
    return dateRange;
  }

  public static getNumberFromString(str: string): number {
    const number = Number(str.replace(/[^0-9.-]+/g, ''))
    return !isNaN(number) ? number : 0;
  }

  public downloadBlobFile(guid: string) {
    return `${this.config?.urlAPI}/documents/document-blob-guid?FileGuid=${guid}`;
  }

  public downloadBlobFileNew(guid: string, inlineOpening = true) {
    return `${this.config?.uploadUrlAPI}/documents/document-blob-guid-nocs?FileGuid=${guid}&fileInline=${inlineOpening}`;
    // return `https://uat.aktravelgroup.com/em/mb/documents/document-blob-guid-nocs?FileGuid=${guid}&fileInline=${inlineOpening}`;
  }

  public downloadExcel(objectKey: string, params?: any) {
    let url = `${this.config?.urlAPI}/io/excel/export?ObjectKey=${objectKey}`;
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          const v = value as any
          if (!!v.plus && !!v.toFormat) {
            url = url + `&${key}=${v.toISODate()}`
          } else {
            url = url + `&${key}=${value}`
          }
        }
      });
    }
    return url
  }
  public currentCompany = new BehaviorSubject<IReservationCompany | undefined>(undefined);

  downloadDocument(fileGuid: string) {
    if (!fileGuid) {
      this.utilityDialogs.error({
        message: 'No file available for download',
        title: 'Download Error',
        size: '380px',
      });
      return;
    }

    try {
      const downloadUrl = this.downloadBlobFileNew(fileGuid);

      // Open the file in a new tab
      window.open(downloadUrl, '_blank');
    } catch (error) {
      this.utilityDialogs.error({
        message: 'Error downloading the file',
        title: 'Download Error',
        size: '380px',
      });
      console.error('Error downloading file:', error);
    }
  }
}
